/**
 * Taxonomy management (design-reference §4.7, UI §6.6). Two sections:
 *
 *  • Categories — the effective taxonomy (base + custom) with live document
 *    counts, inline enable/disable (a disable reports how many documents still
 *    carry the category), and an add/edit drawer.
 *  • Pending suggestions — grouped classifier proposals, each Approve-able into a
 *    category (which then offers an auto-review sweep) or Reject-able.
 *
 * Every number and action comes from a real endpoint: `GET/POST/PATCH
 * /v1/categories`, `GET /v1/category-suggestions`, its approve/reject actions,
 * and `POST /v1/review/sweep`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError } from '../../api/errors';
import type {
  ApprovalResponse,
  CategorySuggestion,
  EffectiveCategory,
} from '../../api/schema';
import { useSession } from '../../auth/session';
import {
  Button,
  Checkbox,
  Drawer,
  ErrorState,
  IconButton,
  Modal,
  Skeleton,
  Tabs,
  Toggle,
  Tooltip,
  useToast,
  type TabDef,
} from '../../components';

import { useTaxonomyApi, type TaxonomyApi } from './api';
import {
  categoryOrigin,
  disableToast,
  documentsLabel,
  exampleSummary,
  findMatchingCategory,
  isSystemFallback,
  matchTooltip,
  turnOnButtonLabel,
} from './format';
import './taxonomy.css';
import { useTaxonomy } from './useTaxonomy';

type DrawerState =
  | { mode: 'add' }
  | { mode: 'edit'; category: EffectiveCategory }
  | null;

const CONFLICT_MESSAGE = 'That was just changed by someone else — refreshed to the latest.';

/** Stable identity so the loading/error branches don't hand `useEffect` a
 * fresh empty array every render. */
const NO_CATEGORIES: EffectiveCategory[] = [];

export function TaxonomyScreen(): React.JSX.Element {
  const api = useTaxonomyApi();
  const { toast } = useToast();
  const { hasPermission } = useSession();
  const canReviewSuggestions = hasPermission('category_suggestions:review');
  const state = useTaxonomy(api, canReviewSuggestions);

  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [approval, setApproval] = useState<ApprovalResponse | null>(null);
  const [busyCategoryId, setBusyCategoryId] = useState<string | null>(null);
  const [sweepingCategoryId, setSweepingCategoryId] = useState<string | null>(null);
  // `?tab=suggestions` deep-links straight to a section (the notification link).
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(
    canReviewSuggestions && searchParams.get('tab') === 'suggestions' ? 'suggestions' : 'categories',
  );
  // Optimistic per-category "enabled" overrides for the toggle, keyed by id.
  // A click flips the switch immediately rather than waiting on the PATCH +
  // reload() round-trip — over a slow connection (or one that's slow because
  // the same tenant is being hammered from several role tabs at once, as in
  // manual QA) the toggle otherwise just sits there for a beat, which reads as
  // "did that even register?" more than once in a row.
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(new Map());

  const reload = state.status === 'ready' || state.status === 'error' ? state.reload : undefined;
  const categories = state.status === 'ready' ? state.data.categories : NO_CATEGORIES;

  // Drop an override the moment the reloaded server truth confirms it, rather
  // than the moment the PATCH resolves — clearing it earlier would flash the
  // row back to its pre-click value for the gap between the PATCH finishing
  // and reload()'s own fetch landing.
  useEffect(() => {
    setOverrides((current) => {
      if (current.size === 0) return current;
      const next = new Map(current);
      let changed = false;
      for (const category of categories) {
        if (next.get(category.id) === category.enabled) {
          next.delete(category.id);
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [categories]);

  /** Run a mutation, translating a 409 into the edit-conflict recovery. Hands
   * back whether it succeeded, so a caller with its own optimistic state can
   * revert it on failure without duplicating the error handling here. */
  const guarded = useCallback(
    async (run: () => Promise<void>): Promise<boolean> => {
      try {
        await run();
        return true;
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) {
          toast(CONFLICT_MESSAGE, { tone: 'error' });
          reload?.();
          return false;
        }
        toast('Something went wrong — please try again.', { tone: 'error' });
        return false;
      }
    },
    [reload, toast],
  );

  const handleToggle = useCallback(
    (category: EffectiveCategory, next: boolean) => {
      if (isSystemFallback(category)) {
        toast(`"Other" is the system fallback and can't be disabled`, { tone: 'error' });
        return;
      }
      setBusyCategoryId(category.id);
      setOverrides((o) => new Map(o).set(category.id, next));
      void guarded(async () => {
        const result = await api.updateCategory(category.id, { enabled: next });
        toast(next ? `"${category.name}" enabled` : disableToast(category.name, result.disabled_impact));
        reload?.();
      })
        .then((ok) => {
          if (!ok) {
            setOverrides((o) => {
              const reverted = new Map(o);
              reverted.delete(category.id);
              return reverted;
            });
          }
        })
        .finally(() => {
          setBusyCategoryId(null);
        });
    },
    [api, guarded, reload, toast],
  );

  const handleReject = useCallback(
    (suggestion: CategorySuggestion) => {
      void guarded(async () => {
        await api.rejectSuggestion(suggestion.id);
        toast('Suggestion rejected — similar documents stay as Other');
        reload?.();
      });
    },
    [api, guarded, reload, toast],
  );

  const handleApprove = useCallback(
    (suggestion: CategorySuggestion) => {
      void guarded(async () => {
        const result = await api.approveSuggestion(suggestion.id);
        toast(`"${result.category.name}" added to the taxonomy`);
        reload?.();
        setApproval(result);
      });
    },
    [api, guarded, reload, toast],
  );

  const handleTurnOn = useCallback(
    (suggestion: CategorySuggestion) => {
      void guarded(async () => {
        const result = await api.turnOnSuggestion(suggestion.id);
        const note =
          result.skipped_document_ids.length > 0
            ? ` (${String(result.skipped_document_ids.length)} already handled elsewhere)`
            : '';
        toast(
          `"${result.category.name}" is on — ${String(result.resolved_document_ids.length)} document(s) classified${note}`,
        );
        reload?.();
      });
    },
    [api, guarded, reload, toast],
  );

  if (state.status === 'loading') return <TaxonomyLoading />;
  if (state.status === 'error') {
    return (
      <section className="ds-tax" aria-label="Taxonomy">
        <ErrorState
          title="We couldn't load your taxonomy"
          body="The taxonomy service didn't respond. Your categories are safe — this only affects this view."
          traceId={state.traceId ?? undefined}
          actions={
            <Button variant="primary" onClick={state.reload}>
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { suggestions } = state.data;

  const tabs: TabDef[] = [
    {
      id: 'categories',
      label: 'Categories',
      content: (
        <CategoriesCard
          categories={categories}
          overrides={overrides}
          busyCategoryId={busyCategoryId}
          sweepingCategoryId={sweepingCategoryId}
          onAdd={() => {
            setDrawer({ mode: 'add' });
          }}
          onEdit={(category) => {
            setDrawer({ mode: 'edit', category });
          }}
          onToggle={handleToggle}
        />
      ),
    },
  ];
  if (canReviewSuggestions) {
    tabs.push({
      id: 'suggestions',
      label: 'Pending suggestions',
      content: (
        <SuggestionsSection
          suggestions={suggestions}
          categories={categories}
          busy={busyCategoryId !== null}
          onApprove={handleApprove}
          onReject={handleReject}
          onTurnOn={handleTurnOn}
        />
      ),
    });
  }

  return (
    <section className="ds-tax" aria-label="Taxonomy">
      <Tabs tabs={tabs} value={tab} onChange={setTab} label="Taxonomy sections" />

      <CategoryDrawer
        drawer={drawer}
        api={api}
        onClose={() => {
          setDrawer(null);
        }}
        onSaved={(message) => {
          setDrawer(null);
          toast(message);
          reload?.();
        }}
      />

      {approval !== null ? (
        <SweepModal
          approval={approval}
          api={api}
          onClose={() => {
            setApproval(null);
          }}
          onSweepStart={(id) => {
            setSweepingCategoryId(id);
          }}
          onSwept={(message) => {
            setSweepingCategoryId(null);
            setApproval(null);
            toast(message);
            reload?.();
          }}
        />
      ) : null}
    </section>
  );
}

// ----------------------------------------------------------------- categories

function CategoriesCard({
  categories,
  overrides,
  busyCategoryId,
  sweepingCategoryId,
  onAdd,
  onEdit,
  onToggle,
}: {
  categories: EffectiveCategory[];
  overrides: ReadonlyMap<string, boolean>;
  busyCategoryId: string | null;
  sweepingCategoryId: string | null;
  onAdd: () => void;
  onEdit: (category: EffectiveCategory) => void;
  onToggle: (category: EffectiveCategory, next: boolean) => void;
}): React.JSX.Element {
  const { hasPermission } = useSession();
  const canManage = hasPermission('categories:manage');

  return (
    <div className="ds-tax-card">
      <header className="ds-tax-card__head">
        <div className="ds-tax-card__titles">
          <h2 className="ds-tax-card__title">Categories</h2>
          <span className="ds-tax-card__sub">{categories.length} in your taxonomy</span>
        </div>
        {canManage ? (
          <Button variant="primary" size="sm" onClick={onAdd}>
            + Add category
          </Button>
        ) : null}
      </header>

      <ul className="ds-tax-category-grid">
        {categories.map((category) => {
          const origin = categoryOrigin(category);
          const sweeping = sweepingCategoryId === category.id;
          return (
            <li key={category.id} className="cat-card">
              <div className="cat-card__top">
                <span className={`ds-origin ds-origin--${origin.toLowerCase()}`}>{origin}</span>
                <span className="cat-card__count">
                  {sweeping ? 'counting…' : documentsLabel(category.document_count)}
                </span>
              </div>
              <p className="cat-card__name">
                {canManage ? (
                  <button
                    type="button"
                    className="cat-card__namebtn"
                    onClick={() => {
                      onEdit(category);
                    }}
                  >
                    {category.name}
                  </button>
                ) : (
                  category.name
                )}
              </p>
              {category.description != null && category.description !== '' ? (
                <Tooltip content={category.description} visualOnly>
                  <button type="button" className="cat-card__desc">
                    {category.description}
                  </button>
                </Tooltip>
              ) : (
                <p className="cat-card__desc cat-card__desc--empty">No description set.</p>
              )}
              <div
                className="cat-card__bottom"
                title={!canManage ? "You don't have permission to change categories" : undefined}
              >
                <span className="cat-card__enabled-label">Enabled</span>
                <Toggle
                  label={`Enable ${category.name}`}
                  checked={overrides.get(category.id) ?? category.enabled}
                  disabled={!canManage || busyCategoryId === category.id}
                  onChange={(next) => {
                    onToggle(category, next);
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      <footer className="ds-tax-card__foot">
        Disabling a category stops new matches — existing documents keep their class; new lookalikes
        route to review.
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------- suggestions

function SuggestionsSection({
  suggestions,
  categories,
  busy,
  onApprove,
  onReject,
  onTurnOn,
}: {
  suggestions: CategorySuggestion[];
  categories: EffectiveCategory[];
  busy: boolean;
  onApprove: (suggestion: CategorySuggestion) => void;
  onReject: (suggestion: CategorySuggestion) => void;
  onTurnOn: (suggestion: CategorySuggestion) => void;
}): React.JSX.Element {
  return (
    <section className="ds-tax-suggest" aria-label="Pending suggestions">
      <header className="ds-tax-suggest__head">
        <h2 className="ds-tax-card__title">Pending suggestions</h2>
        <span className="ds-tax-card__sub">{suggestions.length} proposed by the classifier</span>
      </header>

      {suggestions.length === 0 ? (
        <p className="ds-tax-suggest__empty">
          No pending suggestions — new proposals appear here when the classifier keeps seeing documents
          that fit no category.
        </p>
      ) : (
        <ul className="ds-tax-suggest__grid">
          {suggestions.map((suggestion) => {
            const matched = findMatchingCategory(suggestion.suggested_name, categories);

            return (
              <li key={suggestion.id} className="ds-suggest-card">
                <div className="ds-suggest-card__top">
                  <h3 className="ds-suggest-card__name">{suggestion.suggested_name}</h3>
                  <span className="ds-suggest-card__count">
                    {documentsLabel(suggestion.occurrence_count ?? 0)}
                  </span>
                  {matched !== undefined ? (
                    <Tooltip content={matchTooltip(matched)}>
                      <IconButton
                        label="Matches an existing category"
                        tone="bare"
                        className="ds-suggest-card__warn"
                      >
                        <span aria-hidden="true">⚠</span>
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </div>
                {suggestion.description != null && suggestion.description.length > 0 ? (
                  <p className="ds-suggest-card__desc">{suggestion.description}</p>
                ) : null}
                <p className="ds-suggest-card__examples">{exampleSummary(suggestion)}</p>
                <div className="ds-suggest-card__actions">
                  {matched !== undefined ? (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        onTurnOn(suggestion);
                      }}
                    >
                      {turnOnButtonLabel(matched)}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => {
                        onApprove(suggestion);
                      }}
                    >
                      Approve &amp; sweep
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      onReject(suggestion);
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// --------------------------------------------------------------- add/edit drawer

function CategoryDrawer({
  drawer,
  api,
  onClose,
  onSaved,
}: {
  drawer: DrawerState;
  api: TaxonomyApi;
  onClose: () => void;
  onSaved: (message: string) => void;
}): React.JSX.Element | null {
  if (drawer === null) return null;
  return (
    <CategoryForm
      key={drawer.mode === 'edit' ? drawer.category.id : 'add'}
      drawer={drawer}
      api={api}
      onClose={onClose}
      onSaved={onSaved}
    />
  );
}

function CategoryForm({
  drawer,
  api,
  onClose,
  onSaved,
}: {
  drawer: Exclude<DrawerState, null>;
  api: TaxonomyApi;
  onClose: () => void;
  onSaved: (message: string) => void;
}): React.JSX.Element {
  const editing = drawer.mode === 'edit';
  const [name, setName] = useState(editing ? drawer.category.name : '');
  const [description, setDescription] = useState(
    editing ? (drawer.category.description ?? '') : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = (): void => {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setError('Give the category a name.');
      return;
    }
    const trimmedDescription = description.trim();
    setError(null);
    setSaving(true);
    void (async () => {
      try {
        if (editing) {
          await api.updateCategory(drawer.category.id, {
            name: trimmedName,
            description: trimmedDescription.length > 0 ? trimmedDescription : null,
          });
          onSaved(`"${trimmedName}" updated`);
        } else {
          await api.createCategory({
            name: trimmedName,
            description: trimmedDescription.length > 0 ? trimmedDescription : null,
          });
          onSaved(`"${trimmedName}" added to the taxonomy`);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          setError('A category with that name already exists.');
        } else {
          setError('Something went wrong — please try again.');
        }
        setSaving(false);
      }
    })();
  };

  const title = editing ? 'Edit category' : 'Add category';

  return (
    <Drawer
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} loadingLabel="Saving…" onClick={submit}>
            {editing ? 'Save changes' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="ds-tax-form">
        <label className="ds-field">
          <span className="ds-field__label">Name</span>
          <input
            className="ds-input"
            placeholder="Category name"
            value={name}
            aria-invalid={error !== null ? true : undefined}
            onChange={(event) => {
              setName(event.currentTarget.value);
            }}
          />
        </label>
        <label className="ds-field">
          <span className="ds-field__label">Description</span>
          <textarea
            className="ds-textarea"
            placeholder="Description — tells the classifier what belongs here"
            value={description}
            rows={3}
            onChange={(event) => {
              setDescription(event.currentTarget.value);
            }}
          />
        </label>
        {error !== null ? (
          <p className="ds-tax-form__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Drawer>
  );
}

// --------------------------------------------------------------- sweep modal

function SweepModal({
  approval,
  api,
  onClose,
  onSweepStart,
  onSwept,
}: {
  approval: ApprovalResponse;
  api: TaxonomyApi;
  onClose: () => void;
  onSweepStart: (categoryId: string) => void;
  onSwept: (message: string) => void;
}): React.JSX.Element {
  const categoryId = approval.category.id;
  const name = approval.category.name;
  const [includeOther, setIncludeOther] = useState(approval.sweep.include_classified_other);
  const [affected, setAffected] = useState(approval.sweep.affected);
  const [previewing, setPreviewing] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changeInclude = (next: boolean): void => {
    setIncludeOther(next);
    setPreviewing(true);
    setError(null);
    void (async () => {
      try {
        const preview = await api.sweep({
          category_id: categoryId,
          apply: false,
          include_classified_other: next,
        });
        setAffected(preview.affected ?? 0);
      } catch {
        setError("Couldn't refresh the match count.");
      } finally {
        setPreviewing(false);
      }
    })();
  };

  const run = (): void => {
    setRunning(true);
    setError(null);
    onSweepStart(categoryId);
    void (async () => {
      try {
        const result = await api.sweep({
          category_id: categoryId,
          apply: true,
          include_classified_other: includeOther,
        });
        onSwept(`Sweep complete — ${documentsLabel(result.enqueued ?? 0)} re-classified as "${name}"`);
      } catch {
        setError('The sweep could not be started — please try again.');
        setRunning(false);
      }
    })();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Run an auto-review sweep?"
      width="lg"
      dismissible={!running}
      footer={
        <div className="ds-modal__footer">
          <Button variant="secondary" onClick={onClose} disabled={running}>
            Skip for now
          </Button>
          <Button variant="primary" loading={running} loadingLabel="Sweeping…" onClick={run}>
            Run sweep
          </Button>
        </div>
      }
    >
      <div className="ds-modal__body">
        <p className="ds-sweep__lede">
          <strong>&quot;{name}&quot;</strong> is now in your taxonomy. I can re-classify similar
          documents from their stored OCR text — no re-scanning or re-OCR needed.
        </p>

        <div className="ds-sweep__count" aria-live="polite">
          <span className="ds-sweep__num">{affected.toLocaleString('en-GB')}</span>
          <span className="ds-sweep__caption">
            {previewing ? 'recounting…' : 'documents in the queue and history match and can be re-classified'}
          </span>
        </div>

        <Checkbox
          className="ds-sweep__toggle"
          checked={includeOther}
          disabled={running || previewing}
          onChange={(event) => {
            changeInclude(event.currentTarget.checked);
          }}
        >
          Include documents currently classified as Other
        </Checkbox>

        {error !== null ? (
          <p className="ds-tax-form__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------- loading

function TaxonomyLoading(): React.JSX.Element {
  return (
    <section className="ds-tax" aria-label="Taxonomy" aria-busy="true">
      <div className="ds-tax-card">
        <header className="ds-tax-card__head">
          <Skeleton width={140} height={16} radius={5} />
          <Skeleton width={110} height={28} radius={8} />
        </header>
        <div className="ds-tax-card__skel">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="ds-tax-card__skelrow">
              <Skeleton width="30%" height={12} radius={5} />
              <Skeleton width="55%" height={12} radius={5} />
            </div>
          ))}
        </div>
      </div>
      <p className="ds-tax__loading">Loading your taxonomy…</p>
    </section>
  );
}
